// =============================================================================
// ORB STATE — pure functions deriving the orb's visual state from the
// session signals (status/mode/isSpeaking/frequencies). Extracted from the
// scale/tint logic inline in apps/web/src/components/corpus/VoiceDock.tsx
// (amplitude(), the live/connecting/speaking branches) and generalised into
// a small state machine any theme-aware orb component can render off,
// without depending on any UI framework or styling system.
//
// Kept pure + framework-free so it is trivially unit-testable (no DOM, no
// React render) and reusable by any future orb surface beyond Throughline.
// =============================================================================

import type { LivingMode, LivingStatus } from './useLivingConversation';

/** The orb's calm-voice visual states, named to match the mockup's own
 *  vocabulary (orbQuiet / orbListening / orbActive from throughline-
 *  contract.md's "Screen/state flags"). 'connecting' is an addition: the
 *  mockup's static states did not need to distinguish it, but a live session
 *  does (the brief's orb must show it is opening the line, not just idle vs
 *  live). */
export type OrbVisualState = 'quiet' | 'connecting' | 'listening' | 'speaking';

export interface OrbStateInput {
  status: LivingStatus;
  mode: LivingMode;
  isSpeaking: boolean;
}

/** Derive the single visual state the orb renders from the raw session
 *  signals. Precedence, most to least specific:
 *    1. not live (disconnected/disconnecting) -> quiet
 *    2. connecting -> connecting
 *    3. connected + mode speaking -> speaking
 *    4. connected + mode listening -> listening
 *  `isSpeaking` from the SDK tracks agent audio playback specifically; `mode`
 *  already encodes speaking-vs-listening (mirrors VoiceDock's `const speaking
 *  = mode === 'speaking'`), so mode is the source of truth here and isSpeaking
 *  is accepted for forward-compat / callers who want the raw signal too. */
export function deriveOrbVisualState(input: OrbStateInput): OrbVisualState {
  const live = input.status === 'connected' || input.status === 'connecting';
  if (!live) return 'quiet';
  if (input.status === 'connecting') return 'connecting';
  return input.mode === 'speaking' ? 'speaking' : 'listening';
}

/** Average a Uint8Array of byte magnitudes (0-255) to a 0..1 amplitude.
 *  Empty array (no live audio) -> 0, so a connected-but-silent orb rests
 *  calmly. Verbatim port of VoiceDock's amplitude(). */
export function amplitude(bytes: Uint8Array): number {
  if (!bytes || bytes.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < bytes.length; i += 1) sum += bytes[i];
  return sum / bytes.length / 255;
}

/** The orb's scale factor from the live input/output spectrum: whichever side
 *  is louder drives the pulse (one object for both speakers, matching
 *  VoiceDock's `Math.max(amplitude(input), amplitude(output))`), capped so
 *  the bloom stays calm rather than jarring. Ports VoiceDock's
 *  `1 + Math.min(amp * 1.2, 0.6)` unchanged. */
export function orbScale(input: Uint8Array, output: Uint8Array): number {
  const amp = Math.max(amplitude(input), amplitude(output));
  return 1 + Math.min(amp * 1.2, 0.6);
}

/** The voice hint string shown under the orb, keyed by visual state. Static
 *  copy (the design's calm voice); a consuming app may override individual
 *  strings but this is the sensible default set so no screen ships without
 *  hint text. */
export const ORB_HINTS: Record<OrbVisualState, string> = {
  quiet: 'Speak naturally — pauses are okay.',
  connecting: 'Opening the line…',
  listening: 'Listening — talk, or tap a card to steer.',
  speaking: 'Interrupt any time, just talk.',
};
